/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 27-07-2026
 *
 * Component voor de modeltitel in de bovenbalk (tot golf 4f een Custom Element): de projectcode met de
 * modelnaam, en daarachter het gebruikersvoorvoegsel, de meekijkers en (in debugmodus) de werkmodus en
 * gebruikersnaam. Vervangt `#modelTitle` in canvas.html.
 *
 * De titel komt uit `DlModel`, de werkmodus/meekijkers uit de `SamenwerkingService`, de projectcode
 * en gebruikersnaam sinds golf a1 uit de `SessieService` en het gebruikersvoorvoegsel sinds golf a3
 * uit de `OpslagService`; alleen de debugvlag komt als input van het bouwscherm binnen (uit de
 * `VlaggenService`). De naam wijzigen gebeurt via de modeltoolbar, niet hier.
 */
import {ChangeDetectionStrategy, Component, Input, computed, effect, inject, signal} from '@angular/core';
import type {ViewRef} from '@angular/core';
import {BaseIslandComponent} from '../base-island.component';
import {DlModel} from '../../app/model/dl-model';
import {OpslagService} from '../../app/model/opslag.service';
import {SamenwerkingService} from '../../services/samenwerking.service';
import {SessieService} from '../../services/sessie.service';

@Component({
    selector: 'dl-model-title',
    standalone: true,
    templateUrl: './dl-model-title.component.html',
    styleUrls: ['./dl-model-title.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[class.dl-model-title--lang]': 'langenaam()',
        '[attr.title]': 'dlModel.titel'
    }
})
export class DlModelTitleComponent extends BaseIslandComponent {

    // --- Properties ---

    /**
     * Het modeldocument: bron van de titel.
     */
    protected readonly dlModel = inject(DlModel);

    /**
     * De samenwerk-sessie-state: bron van de werkmodus en de meekijkers.
     */
    protected readonly samenwerking = inject(SamenwerkingService);

    /**
     * De sessie: bron van de projectcode en de gebruikersnaam.
     */
    protected readonly sessie = inject(SessieService);

    /**
     * De opslagketen: bron van het gebruikersvoorvoegsel bij het model.
     */
    protected readonly opslag = inject(OpslagService);

    constructor() {
        super('debug');
        /**
         * Workaround voor de opstartrace: de template-dependency op `dlModel.titel` gaat bij de
         * eerste render verloren (vgl. Angular-issue #61662), waardoor de write bij een nieuw
         * model de view niet meer dirty maakt. Een `markForCheck` hier is niet genoeg: dit effect
         * draait bínnen een scheduler-tick, en een markering tijdens een lopende tick plant geen
         * vervolgtck (`shouldScheduleTick`/`runningTick`). Daarom na de notificatie als microtask
         * — dus ná de lopende tick — een synchrone render van deze view; die herstelt meteen ook
         * de template-dependency, waarna het gewone signal-mechanisme het weer overneemt.
         */
        effect(() => {
            void this.dlModel.titel; //de read waarmee dit effect het titel-signal volgt
            queueMicrotask(() => {
                if (!(this.cdr as ViewRef).destroyed) {
                    this.cdr.detectChanges();
                }
            });
        });
    }

    // --- Inputs ---

    /**
     * Of de debugmodus aan staat.
     */
    @Input()
    set debug(waarde: boolean) {
        this._debug.set(waarde);
        this.cdr.markForCheck();
    }
    protected readonly _debug = signal(false);

    // --- Afgeleide state ---

    /**
     * De ingelogde gebruikersnaam; alleen zichtbaar in debugmodus.
     */
    protected readonly usernaam = computed(() => this.sessie.naam() || '?');

    //we berekenen de lenge even op basis van de signals
    protected langenaam = computed<boolean>(() => {
        //registeer signals
        let lengte = this.dlModel?.titel?.length || 0;
        lengte += this.sessie.projectcode().length + 4; //haken, dubbele punt en spatie
        if (this._debug())
        {
            lengte += this.samenwerking.werkmodus().length + this.usernaam().length + 2;
        }
        if (this.samenwerking.heeftMeekijkers()) {
            lengte += 2; //oogje
        }

        return lengte > 30;
    });

}
